import { useLang } from '../context/LanguageContext';
import {
  AKTUELLES_ITEMS,
  ENTWICKLUNGEN_INITIAL_VISIBLE,
  LEISTUNGEN_ITEMS,
  METHODIK_ITEMS,
  TEAM_INTRO,
  TEAM_MEMBERS,
} from './siteContent';
import {
  AKTUELLES_ITEMS_EN,
  LEISTUNGEN_ITEMS_EN,
  METHODIK_ITEMS_EN,
  TEAM_INTRO_EN,
  TEAM_MEMBERS_EN,
  UI_EN,
} from './siteContent.en';

const UI_DE = {
  heroHeadline: 'Kanzlei für Familienrecht.',
  kanzleiTagline: '',
  kontaktButton: 'Kontakt',
  menuLabel: 'Menü',
  zurueck: 'Zurück',
  mehrErfahren: 'Mehr erfahren',
  lebenslauf: 'Lebenslauf',
  veroeffentlichungen: 'Veröffentlichungen',
  absenden: 'Absenden',
  sending: 'Wird gesendet...',
  sent: 'Vielen Dank, Ihre Nachricht wurde gesendet.',
  error: 'Fehler beim Senden. Bitte versuchen Sie es erneut.',
  form: {
    vorname: 'Vorname',
    nachname: 'Nachname',
    email: 'Email',
    telefon: 'Telefonnummer',
    nachricht: 'Nachricht',
  },
  formPrivacyLead: 'Informationen zur Verarbeitung Ihrer Daten finden Sie in unserer',
  formPrivacyLink: 'Datenschutzerklärung',

  nav: {
    start: 'Start',
    leistungen: 'Leistungen',
    vorgehensweisen: 'Vorgehensweisen',
    team: 'Team',
    aktuelles: 'Aktuelles',
    kontakt: 'Kontakt',
  },

  legal: {
    impressum: 'Impressum',
    datenschutz: 'Datenschutz',
    widerruf: 'Widerrufsbelehrung',
  },

  sections: {
    ueberUns: {
      eyebrow: 'über uns',
      headline: 'Zöllner Lorenz ist eine Münchner Kanzlei für Familienrecht, Mediation und Cooperative Praxis. Wir begleiten Menschen in den bedeutendsten Übergängen ihres Lebens – mit juristischer Präzision und menschlichem\u00A0Verstand.',
      body: 'Unsere Arbeit gründet auf der Überzeugung, dass gute rechtliche Begleitung nicht eskaliert, sondern klärt. Nicht kämpft, sondern ermöglicht. Nicht trennt, was zusammengehört – sondern schützt, was bleibt.',
    },
    methodik: {
      eyebrow: 'vorgehensweisen',
      headlineSans1: 'Mit uns durch',
      headlineSans2: 'schwierige Zeiten',
      headlineSans3: '',
      headlineAccent: 'auf Ihre Art.',
      body: 'Jede Situation ist einzigartig. Wir passen uns Ihren Bedürfnissen an. Gemeinsam finden wir den richtigen Weg.',
      servicesButton: 'Unsere Services',
    },
    leistungen: {
      eyebrow: 'leistungen',
      headlineSans: 'Unser',
      headlineAccent: 'Leistungsangebot',
      intro1: 'Im Familienrecht geht es um weit mehr als juristische Fragen. Es geht um die Gestaltung Ihrer Zukunft.',
      intro2: 'Mit Sitz in der Münchner Innenstadt beraten und vertreten wir Sie in allen Bereichen des Familienrechts – deutschlandweit. Unsere Beratungsleistungen umfassen unter anderem:',
    },
    team: {
      eyebrow: 'team',
      headlineSans: 'Unser',
      headlineAccent: 'Team',
      intro: TEAM_INTRO,
    },
    ueberzeugung: {
      eyebrow: 'Unsere Überzeugung',
      quote: 'Veränderung braucht keine Verlierer.',
      quoteAccent: 'Sie braucht die richtigen Begleiter.',
      body: 'Trennung ist kein Scheitern – sie ist ein Neuanfang. Wir stehen an Ihrer Seite – nicht gegen die andere.',
    },
    aktuelles: {
      eyebrow: 'news',
      headlineSans1: 'Aktuelle',
      headlineSans2: 'Entwicklungen',
      headlineSans3: 'im',
      headlineAccent: 'Familienrecht',
      body: 'Einblicke in Themen, rechtliche Entwicklungen und unsere Sicht auf ein Familienrecht, das Familien trotz Trennung verbindet.',
      carouselPrev: 'Vorherige Beiträge',
      carouselNext: 'Weitere Beiträge',
    },
    kontakt: {
      headline: 'Kontakt',
      intro: 'Schildern Sie uns Ihr Anliegen – wir melden uns zeitnah bei Ihnen.',
    },
  },

  footer: {
    address: 'Maximiliansplatz 12a · 80333 München',
    telefon: 'Telefon',
    email: 'E-Mail',
    termine: 'Termine nach Vereinbarung',
  },
};

export type UIStrings = typeof UI_DE;

export function useTranslation() {
  const { lang } = useLang();
  const t = lang === 'de' ? UI_DE : UI_EN;
  const methodik = lang === 'de' ? METHODIK_ITEMS : METHODIK_ITEMS_EN;
  const team = lang === 'de' ? TEAM_MEMBERS : TEAM_MEMBERS_EN;
  const teamIntro = lang === 'de' ? TEAM_INTRO : TEAM_INTRO_EN;
  const leistungen = lang === 'de' ? LEISTUNGEN_ITEMS : LEISTUNGEN_ITEMS_EN;
  const aktuelles = lang === 'de' ? AKTUELLES_ITEMS : AKTUELLES_ITEMS_EN;

  const NAV_LABELS: Record<string, string> = lang === 'de'
    ? { Start: 'Start', Leistungen: 'Leistungen', Vorgehensweisen: 'Vorgehensweisen', Team: 'Team', Aktuelles: 'Aktuelles', Kontakt: 'Kontakt' }
    : { Start: 'Home', Leistungen: 'Services', Vorgehensweisen: 'Approach', Team: 'Team', Aktuelles: 'News', Kontakt: 'Contact' };

  const LEGAL_LABELS: Record<string, string> = lang === 'de'
    ? { Impressum: 'Impressum', Datenschutz: 'Datenschutz', Widerrufsbelehrung: 'Widerrufsbelehrung', 'Cookie-Einstellungen': 'Cookie-Einstellungen' }
    : { Impressum: 'Legal Notice', Datenschutz: 'Privacy Policy', Widerrufsbelehrung: 'Right of withdrawal', 'Cookie-Einstellungen': 'Cookie settings' };

  const navLabel = (deLabel: string) => NAV_LABELS[deLabel] ?? deLabel;
  const legalLabel = (deLabel: string) => LEGAL_LABELS[deLabel] ?? deLabel;

  return { t, lang, methodik, team, teamIntro, leistungen, aktuelles, ENTWICKLUNGEN_INITIAL_VISIBLE, navLabel, legalLabel };
}
